/**
 * Campaigns — list view of producer-facing campaigns for the active
 * tenant. Post-login landing page. Click a row to open its
 * stat sheet at /statistics/:id.
 *
 * v10.19.8 split: the per-campaign detail/timeline view lives at
 * /statistics/:campaignId in CampaignStatistics.jsx; this page is
 * the pure index.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, CircularProgress, Button,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import api from '../services/api';
import { fmtDate } from '../utils/formatDate';
import CampaignPoster from '../components/CampaignPoster';
import CampaignEditDialog from '../components/CampaignEditDialog';
import PageHeader from '../components/PageHeader';


export default function Campaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  // v10.19.18: campaign being edited via the in-list pencil button.
  // null = no editor open. Operator can edit without first opening
  // the campaign's statistics page.
  const [editing, setEditing] = useState(null);

  const loadList = () => {
    api.get('/api/campaigns')
      .then((r) => setCampaigns(r.data || []))
      .catch(() => setCampaigns([]));
  };

  useEffect(() => { loadList(); }, []);

  const openCampaign = (c) => {
    // v10.19.10: opening a campaign sets it active and lands on the
    // Dashboard, where the full stat sheet renders inline. Direct
    // /statistics/:id is still available for forwarding and bookmarks.
    try { localStorage.setItem('activeCampaignId', String(c.id)); } catch (_e) { /* private mode */ }
    navigate('/');
  };

  // v10.19.55: if there's exactly one campaign and the operator doesn't
  // already have it picked, auto-open it. The Campaigns page is a
  // picker — with one option, the picker is just a click in the way.
  // We keep the page visible when the operator navigates here on
  // purpose (activeCampaignId already matches the sole campaign) so
  // they can still create a second one or edit the existing.
  const [autoRedirecting, setAutoRedirecting] = useState(false);
  useEffect(() => {
    if (!campaigns || campaigns.length !== 1) return;
    let activeId = null;
    try { activeId = localStorage.getItem('activeCampaignId'); } catch (_e) { /* private mode */ }
    if (activeId && String(activeId) === String(campaigns[0].id)) return;
    setAutoRedirecting(true);
    openCampaign(campaigns[0]);
    // openCampaign is a stable inline arrow + navigate is stable from
    // react-router; depending on campaigns alone is sufficient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <PageHeader
        title="Campaigns"
        subtitle="Pick a campaign to unlock the rest of the workspace. Every DCP and watermark screener threads into one so the filmmaker can track every activity end-to-end on a single timeline + stat sheet."
        actions={
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            New campaign
          </Button>
        }
      />
      <CampaignEditDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        campaign={null}
        onSaved={(c) => { loadList(); if (c?.id) { try { localStorage.setItem('activeCampaignId', String(c.id)); } catch (_e) { /* */ } navigate(`/statistics/${c.id}`); } }}
      />
      <CampaignEditDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        campaign={editing}
        onSaved={() => loadList()}
      />

      {(campaigns === null || autoRedirecting) && <CircularProgress size={20} />}
      {!autoRedirecting && campaigns !== null && campaigns.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 1 }}>No campaigns yet.</Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Create one with the "New campaign" button — the rest of the workspace stays locked until a campaign is picked.
          </Typography>
        </Paper>
      )}

      {/* v10.19.54: locked tile width. minmax(N, 1fr) was still letting
          tiles stretch ~50% before the next column threshold, producing
          the same "jump big, shrink, jump big" cycle on resize. Now the
          tile width is a hard 220px on sm+ — only column count changes
          as the window resizes, and whitespace pads the right edge of
          each row instead of stretching the tiles. Mobile keeps minmax
          with a 1fr stretch so 2 cols pack a phone screen cleanly. */}
      {!autoRedirecting && campaigns !== null && campaigns.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 1.5, md: 2.5 },
            justifyContent: { xs: 'stretch', sm: 'start' },
            gridTemplateColumns: {
              xs: 'repeat(auto-fill, minmax(140px, 1fr))',
              sm: 'repeat(auto-fill, 220px)',
            },
          }}
        >
          {campaigns.map((c) => {
            const s = c.summary || {};
            const dateLabel = c.archived_at
              ? `Archived ${fmtDate(c.archived_at)}`
              : `Started ${fmtDate(c.started_at || c.created_at)}`;
            return (
              <Box
                key={c.id}
                onClick={() => openCampaign(c)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  // Plex hover: subtle lift + scale + glow on the
                  // poster only; title block stays anchored.
                  '&:hover .poster-frame': {
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: '0 12px 28px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,160,23,0.55)',
                  },
                  '&:hover .poster-overlay': { opacity: 1 },
                  '&:hover .edit-pencil': { opacity: 1 },
                }}
              >
                <Box
                  className="poster-frame"
                  sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '2 / 3',
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    overflow: 'hidden',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
                    transition: 'transform 180ms ease, box-shadow 180ms ease',
                  }}
                >
                  {c.has_poster ? (
                    <Box sx={{ width: '100%', height: '100%' }}>
                      <CampaignPoster
                        campaignId={c.id}
                        hasPoster
                        orientation={c.poster_orientation}
                        shortEdge={9999}
                        fillContainer
                      />
                    </Box>
                  ) : (
                    <Box sx={{
                      width: '100%', height: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      p: 2, textAlign: 'center',
                      // Subtle gradient so empty tiles still feel like a
                      // poster slot, not a hole.
                      background: 'linear-gradient(180deg, rgba(40,40,40,0.6) 0%, rgba(20,20,20,0.9) 100%)',
                    }}>
                      <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', lineHeight: 1.3 }}>
                        {c.name}
                      </Typography>
                    </Box>
                  )}

                  {/* Hover-reveal stats strip — gradient up from the
                      bottom of the poster like Plex's hover detail. */}
                  <Box
                    className="poster-overlay"
                    sx={{
                      position: 'absolute',
                      left: 0, right: 0, bottom: 0,
                      p: 1,
                      opacity: 0,
                      transition: 'opacity 180ms ease',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0) 100%)',
                      display: 'flex',
                      gap: 0.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Tooltip title={`${s.dcps_delivered_to_venues || 0} DCP playback + ${s.venues_required_emergency_mp4 || 0} emergency MP4`}>
                      <Chip
                        size="small"
                        label={`${s.total_deliveries || 0} DCPs`}
                        color={(s.total_deliveries || 0) > 0 ? 'success' : 'default'}
                        sx={{
                          height: 20, fontSize: '0.65rem',
                          bgcolor: (s.total_deliveries || 0) > 0 ? 'success.main' : 'rgba(255,255,255,0.18)',
                          color: 'common.white',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    </Tooltip>
                    <Chip
                      size="small"
                      label={`${s.bound_watermark_jobs || 0} screeners`}
                      sx={{
                        height: 20, fontSize: '0.65rem',
                        bgcolor: 'rgba(255,255,255,0.18)',
                        color: 'common.white',
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  </Box>

                  {/* Hover-reveal edit pencil, top-right. */}
                  <Tooltip title="Edit campaign">
                    <IconButton
                      className="edit-pencil"
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setEditing(c); }}
                      sx={{
                        position: 'absolute',
                        top: 4, right: 4,
                        opacity: 0,
                        transition: 'opacity 180ms ease',
                        bgcolor: 'rgba(0,0,0,0.55)',
                        color: 'common.white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box sx={{ px: 0.25, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={c.name}
                  >
                    {c.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}>
                    {dateLabel}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
